import { useEffect, useRef } from "react";
import { Animated, Easing } from "react-native";

import { playbackFraction } from "@/playback/playbackProgress";

type SmoothPlaybackProgressOptions = {
  currentTime: number;
  duration: number;
  freezeWhilePaused?: boolean;
  itemKey: string | null;
  mode?: "authoritative" | "glide";
  playing: boolean;
  sampleIntervalMs: number;
};

/**
 * Interpolates sparse player/receiver samples for display only. Queue state,
 * seeking, resume persistence, and completion continue to use authoritative
 * timestamps supplied by the native player or receiver.
 */
export function useSmoothPlaybackProgress({
  currentTime,
  duration,
  itemKey,
  mode = "authoritative",
  playing,
  sampleIntervalMs
}: SmoothPlaybackProgressOptions) {
  const progress = useRef(
    new Animated.Value(playbackFraction(currentTime, duration))
  ).current;
  const itemKeyRef = useRef(itemKey);

  useEffect(() => {
    const fraction = playbackFraction(currentTime, duration);
    const itemChanged = itemKeyRef.current !== itemKey;
    itemKeyRef.current = itemKey;
    progress.stopAnimation();

    // The actual player timeline always follows authoritative time directly.
    // Secondary progress decorations may glide *to* the latest confirmed
    // sample. They never predict a future timestamp, so receiver buffering
    // cannot make them run ahead of the media.
    if (mode === "authoritative" || !playing || itemChanged || duration <= 0) {
      progress.setValue(fraction);
      return;
    }

    const animation = Animated.timing(progress, {
      duration: sampleIntervalMs,
      easing: Easing.linear,
      toValue: fraction,
      useNativeDriver: false
    });
    animation.start();
    return () => animation.stop();
  }, [currentTime, duration, itemKey, mode, playing, progress, sampleIntervalMs]);

  return progress;
}
